import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "../../components/Button";
import { Field } from "../../components/Field";
import { PhoneInput } from "../../components/PhoneInput";
import { OnboardingLayout } from "./OnboardingLayout";
import { DEFAULT_COUNTRY, normalisePhone, phoneError, type CountryDialInfo } from "../../lib/phone";
import { requestParentConsent, type ConsentChannel } from "../../api/onboarding";
import { ApiError } from "../../api/client";

interface ParentPhoneScreenProps {
  /** The student's own number, which a parent's may not equal. */
  studentPhone?: string | undefined;
  /** The student's own email, for the same reason. */
  studentEmail?: string | undefined;
}

const CHANNELS: { id: ConsentChannel; label: string; verb: string }[] = [
  { id: "sms", label: "Text", verb: "Text my parent" },
  { id: "whatsapp", label: "WhatsApp", verb: "Message my parent" },
  { id: "email", label: "Email", verb: "Email my parent" },
];

/**
 * Screen 5 — minors only, and outside the step dots.
 *
 * The student's own contact is blocked on every channel. Without that check a
 * student can "consent" for themselves in ten seconds, and the whole NDPA gate
 * becomes decoration — so blocking the phone while allowing their own email
 * would have been no gate at all.
 *
 * Three channels rather than one. A parent who does not read texts is a
 * student who never gets activated, and the choice belongs to the person who
 * knows how their parent actually communicates.
 */
export function ParentPhoneScreen({ studentPhone, studentEmail }: ParentPhoneScreenProps) {
  const navigate = useNavigate();
  const [channel, setChannel] = useState<ConsentChannel>("sms");
  const [country, setCountry] = useState<CountryDialInfo>(DEFAULT_COUNTRY);
  const [digits, setDigits] = useState("");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const byEmail = channel === "email";
  const normalised = normalisePhone(digits, country);
  const trimmedEmail = email.trim().toLowerCase();

  const isOwnNumber = Boolean(studentPhone && normalised && normalised === studentPhone);
  const isOwnEmail = Boolean(
    studentEmail && trimmedEmail && trimmedEmail === studentEmail.trim().toLowerCase(),
  );

  const emailProblem = !trimmedEmail
    ? "Enter your parent's email address."
    : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)
      ? "That doesn't look like an email address."
      : isOwnEmail
        ? "That's your email — we need a parent or guardian's."
        : null;

  const phoneProblem = isOwnNumber
    ? "That's your number — we need a parent or guardian's."
    : phoneError(digits, country);

  const problem = byEmail ? emailProblem : phoneProblem;
  const error = failure ?? (submitted ? problem : null);

  /**
   * Actually send it.
   *
   * This screen collected a number and navigated to a waiting room that nobody
   * was ever going to leave: no message was sent, no consent record existed to
   * approve. `ConsentService` had been there the whole time with no route
   * reaching it and nothing enqueuing the link.
   */
  const submit = async () => {
    setSubmitted(true);
    setFailure(null);
    if (problem !== null || sending) return;

    setSending(true);
    try {
      const result = await requestParentConsent({
        channel,
        ...(byEmail ? { parentEmail: trimmedEmail } : { parentPhone: normalised ?? "" }),
      });

      /*
       * The server sends the link on every channel. It used to come back here
       * for the student to share from their own messaging app, which meant the
       * student held the approval link and could open it themselves.
       */
      void navigate("/waiting", {
        state: {
          sentTo: result.sentTo,
          channel: result.channel,
          canResendAt: result.canResendAt,
        },
      });
    } catch (caught) {
      setFailure(
        caught instanceof ApiError
          ? caught.message
          : "We couldn't reach that contact. Check it and try again.",
      );
    } finally {
      setSending(false);
    }
  };

  const action = CHANNELS.find((option) => option.id === channel)?.verb ?? "Send";

  return (
    <OnboardingLayout
      title="How should we reach your parent?"
      subtitle="Because you're under 18, a parent or guardian has to say yes before you start. We'll send them one message with a link — that's all."
      action={
        <Button disabled={sending} onClick={() => void submit()}>
          {sending ? "Sending…" : action}
        </Button>
      }
    >
      {/*
        The student picks, because they know which one their parent actually
        reads. A link sent down a channel nobody checks is an account that
        never activates.
      */}
      <div
        role="group"
        aria-label="How to reach them"
        className="flex gap-1 rounded-[var(--radius-chip)] border border-border bg-surface p-1"
      >
        {CHANNELS.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={channel === option.id}
            onClick={() => {
              setChannel(option.id);
              setSubmitted(false);
              setFailure(null);
            }}
            className={`min-h-10 flex-1 rounded-[var(--radius-chip)] px-3 text-[12.5px] font-semibold transition-colors ${
              channel === option.id ? "bg-accent-tint text-text" : "text-text-muted"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {byEmail ? (
        <Field
          label="Parent's email address"
          htmlFor="parent-email"
          error={error}
          hint="Use the address they actually check."
        >
          <input
            id="parent-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-invalid={Boolean(error)}
            placeholder="parent@example.com"
            className={`min-h-12 w-full rounded-[var(--radius-control)] border bg-surface px-3.5 text-[15px] text-text outline-none placeholder:text-text-muted focus:border-accent ${
              error ? "border-negative" : "border-border-strong"
            }`}
          />
        </Field>
      ) : (
        <Field
          label={channel === "whatsapp" ? "Parent's WhatsApp number" : "Parent's phone number"}
          htmlFor="parent-phone"
          error={error}
          hint={
            channel === "whatsapp"
              ? "Use the number they have WhatsApp on."
              : "Use the number they read texts on."
          }
        >
          <PhoneInput
            id="parent-phone"
            value={digits}
            onChange={setDigits}
            country={country}
            onCountryChange={setCountry}
            invalid={Boolean(error)}
          />
        </Field>
      )}

      {/* Showing the parent's message to the student is a trust move: they can
          see nothing embarrassing is being sent on their behalf. */}
      <div className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">
          What they'll get
        </p>
        {channel === "email" ? (
          <div className="flex flex-col gap-2 text-[13px] leading-[1.55]">
            <p className="m-0">Your child signed up for PrepAI to practise for JAMB and WAEC.</p>
            <p className="m-0">
              Because they're under 18, we need a parent or guardian to say yes before they can
              start.
            </p>
            <p className="m-0">
              Tap the button below to approve or decline — it takes less than a minute.
            </p>
            <p className="m-0 text-[12px] text-text-muted">
              The link expires in a few days. If you weren't expecting this, you can ignore it.
            </p>
          </div>
        ) : (
          <p className="m-0 text-[13px] leading-[1.55]">
            Your child signed up for PrepAI (JAMB/WAEC practice) and needs a parent's OK to start.
            Tap the link in the message to approve — it takes less than a minute.
          </p>
        )}
      </div>

      <p className="m-0 text-[12.5px] leading-[1.55] text-text-muted">
        One tap on their side. This screen updates the moment they say yes.
      </p>
    </OnboardingLayout>
  );
}
